import React, { useState, useRef, useCallback } from 'react';
import { Flex } from '@withjoy/joykit';
import { Hamburger } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useOverrides } from '@shared/utils/overrides';
import { animationTransition } from '@shared/utils/animationTransition';
import { UniversalBurgerMenu } from '@apps/admin/components/UniversalBurgerMenu/UniversalBurgerMenu';
import type { ParticleJoyLogoHandle } from '@shared/components/ParticleJoyLogo';
import {
  StyledRoot,
  StyledLeftSection,
  StyledRightSection,
  StyledTitle,
  StyledParticleJoyLogo,
  StyledMenuTriggerZone,
  StyledHamburgerWrapper,
  HEADER_HEIGHT,
  HEADER_HEIGHT_FIXED,
  MOBILE_NAV_ROW_HEIGHT,
} from './AdminHeaderBar.styles';
import { AdminHeaderBarSpacer } from './AdminHeaderBarSpacer';
import { AdminHeaderBarSurface } from './AdminHeaderBarSurface';
import type { AdminHeaderBarProps } from './AdminHeaderBar.types';
import { CommandMenuSearchButton } from '@apps/admin/components/AdminCommandMenu/CommandMenuSearchButton';

/**
 * AdminHeaderBar - Consolidated header component for admin routes.
 *
 * Features:
 * - Composable slots for navigation and actions
 * - Fixed, static, or sticky positioning
 * - Responsive or fixed height
 * - Configurable bottom border
 * - Support for native webview (hide hamburger/logo)
 * - Style overrides via useOverrides pattern
 *
 * @example
 * // Fixed header with spacer (WebsiteOverview)
 * <AdminHeaderBar
 *   title="Website"
 *   navigation={<WebsiteTabNav />}
 *   actions={<HeaderQuickAccessLinks />}
 * />
 * <AdminHeaderBar.Spacer />
 *
 * @example
 * // Static header (WebsiteDesignerV2)
 * <AdminHeaderBar
 *   title="Design"
 *   position="static"
 *   height="fixed"
 *   showBorder
 *   navigation={<WebsiteTabNav />}
 *   actions={<DesignerActionBar />}
 * />
 *
 * @example
 * // Sticky header (Registry)
 * <AdminHeaderBar
 *   title="Registry"
 *   position="sticky"
 *   navigation={<RegistryTabNav />}
 *   actions={<HelpLinks />}
 * />
 */
const AdminHeaderBarComponent: React.FC<AdminHeaderBarProps> = props => {
  const {
    title,
    titleAdornment,
    navigation,
    actions,
    mobileNavigation = 'auto',
    position = 'fixed',
    height = 'responsive',
    hideHamburger = false,
    nativePadding = false,
    hideLogo = false,
    hideTitle = false,
    hideSearchButton = false,
    showBorder = false,
    onTitleClick,
    overrides = {},
    'aria-label': ariaLabel,
    'data-testid': testId = 'admin-header-bar',
  } = props;

  const showMobileNav = !!navigation && mobileNavigation !== 'desktop-only';
  const hasMainRowContent = !hideHamburger || !hideLogo || !hideTitle || !hideSearchButton || !!actions;

  const { tq } = useTranslation('adminHeaderBar');
  const [isHovered, setIsHovered] = useState(false);
  const particleLogoRef = useRef<ParticleJoyLogoHandle>(null);

  const {
    Root: [Root, rootProps],
    LeftSection: [LeftSection, leftSectionProps],
    RightSection: [RightSection, rightSectionProps],
    Title: [Title, titleProps],
  } = useOverrides(
    {
      Root: StyledRoot,
      LeftSection: StyledLeftSection,
      RightSection: StyledRightSection,
      Title: StyledTitle,
    },
    overrides,
  );

  const desktopHeight = height === 'fixed' ? HEADER_HEIGHT_FIXED : HEADER_HEIGHT.md;
  const heightStyles = showMobileNav
    ? hasMainRowContent
      ? { minHeight: { _: HEADER_HEIGHT.base }, height: { md: desktopHeight } }
      : {}
    : height === 'fixed'
      ? { height: HEADER_HEIGHT_FIXED }
      : {
          height: { _: HEADER_HEIGHT.base, md: HEADER_HEIGHT.md },
          transition: animationTransition('height'),
        };

  const positionStyles =
    position === 'fixed'
      ? {
          position: 'fixed' as const,
          backdropFilter: { _: 'blur(6px)', md: 'blur(12px)' },
        }
      : position === 'sticky'
        ? {
            position: 'sticky' as const,
            top: 0,
          }
        : {};

  const handleTriggerZoneMouseMove = useCallback(() => {
    if (!hideLogo && particleLogoRef.current) {
      particleLogoRef.current.triggerParticles();
    }
  }, [hideLogo]);

  return (
    <Root
      as="header"
      role="banner"
      aria-label={ariaLabel ?? (typeof title === 'string' && title ? `${title} admin header` : tq`Admin header`)}
      data-testid={testId}
      display="flex"
      flexWrap={showMobileNav ? { _: 'wrap', md: 'nowrap' } : 'nowrap'}
      width="100%"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="white"
      zIndex="docked"
      paddingLeft={5}
      paddingRight={5}
      {...(nativePadding && { paddingLeft: 6 })}
      {...(showBorder && {
        borderBottom: '1px solid',
        borderColor: 'mono3',
      })}
      {...heightStyles}
      {...positionStyles}
      {...rootProps}
    >
      <LeftSection
        as="div"
        display="flex"
        gap={8}
        alignItems="center"
        order={1}
        data-testid="admin-header-bar-left"
        {...(showMobileNav && hasMainRowContent && { minHeight: { _: HEADER_HEIGHT.base, md: 'auto' } })}
        {...leftSectionProps}
      >
        {(!hideHamburger || !hideLogo || !hideTitle) && (
          <Flex
            alignItems="center"
            columnGap={5}
            zIndex={1}
          >
            {(!hideHamburger || !hideLogo) && (
              <UniversalBurgerMenu>
                {({ isOpen }: { isOpen: boolean }) => (
                  <StyledMenuTriggerZone
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onMouseMove={handleTriggerZoneMouseMove}
                    data-testid="menu-trigger-zone"
                  >
                    {!hideHamburger && (
                      <StyledHamburgerWrapper
                        $isHovered={isHovered}
                        $isActive={isOpen}
                      >
                        <Hamburger size={32} />
                      </StyledHamburgerWrapper>
                    )}
                    {!hideLogo && (
                      <StyledParticleJoyLogo
                        ref={particleLogoRef}
                        id="admin-header-joy-logo"
                        logoColor="#1D1D1F"
                      />
                    )}
                  </StyledMenuTriggerZone>
                )}
              </UniversalBurgerMenu>
            )}
            {!hideTitle && (
              <Flex
                alignItems="center"
                gap={6}
              >
                <Title
                  typographyVariant="hed5"
                  fontWeight="semibold"
                  color="mono14"
                  data-testid="admin-header-bar-title"
                  {...(onTitleClick && { cursor: 'pointer', onClick: onTitleClick })}
                  {...titleProps}
                >
                  {title}
                </Title>
                {titleAdornment}
              </Flex>
            )}
          </Flex>
        )}
        {navigation && !showMobileNav && <Flex display={{ _: 'none', md: 'flex' }}>{navigation}</Flex>}
      </LeftSection>
      {showMobileNav && (
        <Flex
          alignItems="center"
          justifyContent={{ _: 'center', md: 'flex-start' }}
          order={{ _: 3, md: 1 }}
          flex={{ _: '0 0 100%', md: '1 1 auto' }}
          minWidth={0}
          height={{ _: pxToRem(MOBILE_NAV_ROW_HEIGHT), md: 'auto' }}
          marginLeft={{ md: 8 }}
          data-testid="admin-header-bar-nav"
        >
          {navigation}
        </Flex>
      )}
      <RightSection
        as="div"
        display="flex"
        alignItems="center"
        flexShrink={1}
        paddingLeft={[4, 6]}
        columnGap={{ _: 0, md: 3 }}
        order={2}
        data-testid="admin-header-bar-right"
        {...rightSectionProps}
      >
        {!hideSearchButton && <CommandMenuSearchButton />}
        {actions}
      </RightSection>
    </Root>
  );
};

AdminHeaderBarComponent.displayName = 'AdminHeaderBar';

/**
 * AdminHeaderBar with attached sub-components.
 *
 * - `<AdminHeaderBar.Spacer />` — push content below a fixed header.
 * - `<AdminHeaderBar.Surface />` — frosted-glass wrapper for multi-tier
 *   sticky headers (use with `position="static"` on AdminHeaderBar).
 */
export const AdminHeaderBar = Object.assign(AdminHeaderBarComponent, {
  Spacer: AdminHeaderBarSpacer,
  Surface: AdminHeaderBarSurface,
});
